import { z } from "zod";
import type {
  ActionDto,
  AssignmentDto,
  FindingDto,
  GroupDto,
  ParticipantDto,
  SectorDto,
  SubjectPublicDto,
} from "./dto";
import {
  FindingCategory,
  NetworkState,
  ParticipantStatus,
  Platform,
  PositionSource,
  SessionState,
  TrackingMode,
  TrackingState,
  UnitType,
} from "./enums";
import type { ActionEvent } from "./events";
import { FindingIcon } from "./finding-icons";
import {
  Latitude,
  Longitude,
  type MultiLineStringGeometry,
  type MultiPolygonGeometry,
} from "./geo";

// Phone <-> server protocol (docs/03, ADR-0004). One path for all data from the phone:
// event -> SQLite outbox -> POST /api/v1/sync. The server is idempotent on (session, event id).

/** Bump when a change breaks compatibility. The server accepts the last two versions. */
export const PROTOCOL_VERSION = 1;
export const SUPPORTED_PROTOCOLS: readonly number[] = [1];

/** Battery level at which HQ is warned (panel) and the phone itself warns (phone-alerts.ts). */
export const LOW_BATTERY_PCT = 20;

export const SYNC_LIMITS = {
  /** Max events in one request; the phone adapts batch size down to 100 on a weak link. */
  maxEventsPerRequest: 1000,
  /** Max downlink events in one response; `hasMore` tells the phone to ask again at once. */
  maxDownlinkEvents: 200,
  /** Request body limit after decompression. */
  maxBodyBytes: 1024 * 1024,
} as const;

/** Photo upload (PUT /api/field/photos/:photoId, issue #91). The phone sends ~0.2 MB. */
export const PHOTO_LIMITS = { maxUploadBytes: 4 * 1024 * 1024 } as const;

const Timestamp = z.iso.datetime({ offset: true });

// Generous bounds that reject only impossible values. Without them a value like 1e39 passes
// validation, overflows a float4 column and fails the whole batch on every retry (spike S4).
/** Horizontal accuracy in metres. iOS can report thousands. */
const AccuracyM = z.number().nonnegative().max(1_000_000);

const uplinkBase = {
  /** UUID generated on the phone; the idempotency key. */
  id: z.uuid(),
  /** Monotonic per-session counter on the phone. Ordering uses (session, seq), never clocks. */
  seq: z.number().int().nonnegative(),
  /** Device clock. The server stores it and a corrected copy. */
  recordedAt: Timestamp,
};

export const PositionEvent = z.object({
  ...uplinkBase,
  type: z.literal("position"),
  lat: Latitude,
  lon: Longitude,
  acc: AccuracyM.nullish(),
  /** Metres above sea level. */
  alt: z.number().min(-20_000).max(100_000).nullish(),
  /** Metres per second. */
  spd: z.number().nonnegative().max(10_000).nullish(),
  hdg: z.number().min(0).max(360).nullish(),
  bat: z.number().int().min(0).max(100).nullish(),
  net: NetworkState.nullish(),
  status: ParticipantStatus,
  /** The unit at this fix (#205); older apps send none and the participant's unit applies. */
  unit: UnitType.optional(),
  src: PositionSource.default("phone"),
});
export type PositionEvent = z.infer<typeof PositionEvent>;

export const StatusEvent = z.object({
  ...uplinkBase,
  type: z.literal("status"),
  status: ParticipantStatus.exclude(["sos"]),
  /** A change of unit (#205) goes with the current status, under the same newest-wins rule. */
  unit: UnitType.optional(),
});
export type StatusEvent = z.infer<typeof StatusEvent>;

export const MessageEvent = z
  .object({
    ...uplinkBase,
    type: z.literal("message"),
    /** "action" or "group:<groupId>"; the server rejects groups the sender is not in. */
    channel: z.string().max(80),
    /** May be empty only with a photo. */
    body: z.string().trim().max(2000),
    lat: Latitude.nullish(),
    lon: Longitude.nullish(),
    hdg: z.number().min(0).max(360).nullish(),
    /**
     * Photo from the app's camera (issue #91), uploaded separately once this event is accepted
     * (PUT /api/field/photos/:photoId). A photo always carries the position it was taken at.
     */
    photoId: z.uuid().optional(),
  })
  .refine((e) => e.body.length > 0 || e.photoId !== undefined, {
    path: ["body"],
    message: "Empty message",
  })
  .refine((e) => e.photoId === undefined || (e.lat != null && e.lon != null), {
    path: ["photoId"],
    message: "A photo needs the position it was taken at",
  });
export type MessageEvent = z.infer<typeof MessageEvent>;

export const FindingEvent = z.object({
  ...uplinkBase,
  type: z.literal("finding"),
  lat: Latitude,
  lon: Longitude,
  category: FindingCategory,
  /** Since issue #160; older apps send only the category. */
  icon: FindingIcon.optional(),
  description: z.string().max(2000).nullish(),
  /** Photo from the app's camera, uploaded like a chat photo (MessageEvent.photoId). */
  photoId: z.uuid().optional(),
});
export type FindingEvent = z.infer<typeof FindingEvent>;

export const SosEvent = z.object({
  ...uplinkBase,
  type: z.literal("sos"),
  lat: Latitude.nullish(),
  lon: Longitude.nullish(),
  acc: AccuracyM.nullish(),
});
export type SosEvent = z.infer<typeof SosEvent>;

export const SosCancelEvent = z.object({
  ...uplinkBase,
  type: z.literal("sos_cancel"),
  /** The `id` of the phone's own `sos` event. */
  sosEventId: z.uuid(),
});
export type SosCancelEvent = z.infer<typeof SosCancelEvent>;

export const RecallAckEvent = z.object({
  ...uplinkBase,
  type: z.literal("recall_ack"),
  recallId: z.uuid(),
});
export type RecallAckEvent = z.infer<typeof RecallAckEvent>;

export const UplinkEvent = z.discriminatedUnion("type", [
  PositionEvent,
  StatusEvent,
  MessageEvent,
  FindingEvent,
  SosEvent,
  SosCancelEvent,
  RecallAckEvent,
]);
export type UplinkEvent = z.infer<typeof UplinkEvent>;
export type UplinkEventType = UplinkEvent["type"];
/** An event before the outbox assigns id, seq and recordedAt. */
export type UplinkEventDraft = UplinkEvent extends infer E
  ? E extends UplinkEvent
    ? Omit<E, "id" | "seq" | "recordedAt">
    : never
  : never;

/** Order in which the phone fills a batch. */
export const UPLINK_PRIORITY: Record<UplinkEventType, number> = {
  sos: 0,
  sos_cancel: 0,
  status: 1,
  recall_ack: 1,
  finding: 2,
  message: 3,
  position: 4,
};

export const SyncRequest = z.object({
  protocol: z.number().int(),
  deviceId: z.string().min(8).max(128),
  appVersion: z.string().max(40),
  /** Highest downlink `seq` the phone has applied. */
  cursor: z.number().int().nonnegative(),
  deviceNow: Timestamp,
  trackingState: TrackingState,
  /**
   * FCM registration token of the phone (ADR-0015): the server sends a data-only "sync now"
   * push when HQ writes something for this responder. Absent: keep the stored one.
   */
  pushToken: z.string().min(1).max(4096).optional(),
  /**
   * Battery now, 0–100, at every sync: HQ must see it also while the track is not recorded
   * (no positions), to remind a responder in time (issue #109). Absent: the phone cannot tell.
   */
  batteryPct: z.number().int().min(0).max(100).optional(),
  /** Events still waiting in the outbox after this batch; shown in the panel. */
  pendingEvents: z.number().int().nonnegative().optional(),
  /** Validated one by one on the server: a bad event goes to quarantine, not the whole batch. */
  events: z.array(z.unknown()).max(SYNC_LIMITS.maxEventsPerRequest),
});
export type SyncRequest = z.infer<typeof SyncRequest>;
export type SyncRequestInput = Omit<SyncRequest, "events"> & { events: UplinkEvent[] };

export const Peer = z.object({
  participantId: z.uuid(),
  name: z.string(),
  groupId: z.uuid().nullable(),
  lat: z.number(),
  lon: z.number(),
  accuracyM: z.number().nullable(),
  status: ParticipantStatus,
  at: Timestamp,
  /**
   * The peer's unit (#269). Optional for forward compatibility: an older server sends none, and
   * the phone then draws an ordinary marker. A drone is `uav`, drawn apart on the map (map-data.ts).
   */
  unitType: UnitType.optional(),
});
export type Peer = z.infer<typeof Peer>;

/**
 * Sync intervals (docs/03 SLO table): sent by the server in hints, and used by a phone that
 * joined offline until the server answers for the first time.
 */
export const SYNC_INTERVAL_S = { normal: 15, live: 3 } as const;

export const SyncHints = z.object({
  syncIntervalS: z.number().int().positive(),
  batchMax: z.number().int().positive(),
  trackingMode: TrackingMode,
  sessionState: SessionState,
  /** Server restored from backup: resend own events with seq >= this value. */
  resyncFrom: z.number().int().nonnegative().nullable(),
});
export type SyncHints = z.infer<typeof SyncHints>;

export const RejectReason = z.enum([
  "schema",
  "forbidden_channel",
  "unknown_reference",
  "after_revocation",
  "not_approved",
  /** The photo id already belongs to another message or finding (double tap, broken client). */
  "duplicate_photo",
]);
export type RejectReason = z.infer<typeof RejectReason>;

export type SyncResponse = {
  /** Ids accepted now or earlier (duplicates included). Mark as acked in the outbox. */
  acked: string[];
  /** Ids the server will never accept. Kept in server quarantine; do not resend. */
  rejected: { id: string | null; reason: RejectReason; detail?: string }[];
  /** Downlink: action events visible to this participant with seq > request cursor. */
  events: ActionEvent[];
  cursor: number;
  hasMore: boolean;
  peers: Peer[];
  serverNow: string;
  clockOffsetS: number;
  hints: SyncHints;
};

/**
 * What a responder sees of their group's sector (issue #134), from
 * `GET /api/field/sectors/:sectorId/view`: the server's coverage and where group mates searched.
 * Fetched while the map is open, with an ETag; kept on the phone for offline use.
 */
export type SectorView = {
  sectorId: string;
  /** "Pokrycie śladem (szacunkowe)", computed by the server (ADR-0009). */
  coveredPct: number;
  computedAt: string | null;
  /** Simplified to about 2 m for the phone. */
  covered: MultiPolygonGeometry | null;
  /** Searching tracks of group mates (not mine: the phone draws its own) near the sector. */
  tracks: { participantId: string; name: string; geometry: MultiLineStringGeometry }[];
};

/**
 * A drone followed on a responder's map (issue #343), from
 * `GET /api/field/drones/:participantId/view`: where it searched, the ground its band covered in
 * the action's sectors and its own share of each sector. Only for that view: the sector coverage
 * (SectorView, panel) counts the drone together with everyone else.
 */
export type DroneView = {
  participantId: string;
  /** Searching segments, not too fast (the ones that count), simplified to about 2 m. */
  track: MultiLineStringGeometry | null;
  /** The drone's band clipped to the action's sectors. */
  covered: MultiPolygonGeometry | null;
  /** "Pokrycie śladem (szacunkowe)" of the drone alone, only sectors it reached. */
  sectors: { sectorId: string; coveredPct: number }[];
};

// --- Join ----------------------------------------------------------------------------------

export const DeviceInfo = z.object({
  deviceId: z.string().min(8).max(128),
  platform: Platform,
  appVersion: z.string().max(40),
  pushToken: z.string().max(512).nullish(),
});
export type DeviceInfo = z.infer<typeof DeviceInfo>;

export const JoinRequest = z.object({
  protocol: z.number().int(),
  /** Generated once per join attempt; a retry with the same id returns the same session. */
  joinId: z.uuid(),
  /** Invite token from the QR fragment (base64url, 32 bytes). */
  token: z.string().min(20).max(200),
  displayName: z.string().trim().min(2).max(60),
  phone: z.string().trim().max(30).nullish(),
  unitType: UnitType,
  device: DeviceInfo,
});
export type JoinRequest = z.infer<typeof JoinRequest>;

/** Everything a phone needs to work offline after joining. */
export type ActionSnapshotForPhone = {
  action: ActionDto;
  groups: GroupDto[];
  sectors: SectorDto[];
  assignments: AssignmentDto[];
  subject: SubjectPublicDto | null;
  /** Findings HQ entered (issue #132), shown on every phone; responders' reports are not here. */
  findings: FindingDto[];
  /** seq of the snapshot; use as the initial sync cursor. */
  snapshotSeq: number;
};

export type JoinResponse = {
  sessionToken: string;
  sessionId: string;
  participant: ParticipantDto;
  sessionState: z.infer<typeof SessionState>;
  snapshot: ActionSnapshotForPhone | null;
  hints: SyncHints;
};
